import React, { useState, useRef, useEffect } from 'react';
import { CalendarDaysIcon, ClockIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

/**
 * DatePicker / TimePicker — DS-styled date & time fields (REQ-SYS-CTRL-01).
 *
 * Uses custom popover pickers (no native browser chrome) to ensure consistent
 * styling with the BIDIT design system. Selected states use brand red, not blue.
 *
 * Values are ISO strings:  DatePicker → "YYYY-MM-DD",  TimePicker → "HH:mm".
 */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startPad = first.getDay();
  const days = [];
  for (let i = startPad - 1; i >= 0; i--) days.push({ date: new Date(year, month, -i), currentMonth: false });
  for (let d = 1; d <= last.getDate(); d++) days.push({ date: new Date(year, month, d), currentMonth: true });
  while (days.length < 42) {
    const next = new Date(days[days.length - 1].date);
    next.setDate(next.getDate() + 1);
    days.push({ date: next, currentMonth: false });
  }
  return days;
}

function parseDate(val) {
  if (!val) return null;
  if (val instanceof Date) return val;
  const [y, m, d] = val.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function formatISO(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// ─── DatePicker ──────────────────────────────────────────────────────────────

// Natural width of the month grid: 7 × ~34px day cells + gaps + padding.
const POPOUT_MIN_WIDTH = 280;

// IDI-518: optional `max` ("YYYY-MM-DD") — later days render disabled.
export function DatePicker({ value, onChange, label, error, className, disabled, max, ...rest }) {
  const [open, setOpen] = useState(false);
  const parsed = parseDate(value);
  const [viewYear, setViewYear] = useState(() => (parsed || new Date()).getFullYear());
  const [viewMonth, setViewMonth] = useState(() => (parsed || new Date()).getMonth());
  const ref = useRef(null);
  const [alignRight, setAlignRight] = useState(false);

  // IDI-537: the pop-out is never narrower than the calendar's natural width;
  // when left-anchoring would run off the viewport, anchor it to the right.
  const toggleOpen = () => {
    if (!open && ref.current && typeof window !== 'undefined') {
      const r = ref.current.getBoundingClientRect();
      const w = Math.max(r.width, POPOUT_MIN_WIDTH);
      setAlignRight(r.left + w > window.innerWidth - 8 && r.right - w >= 0);
    }
    setOpen(o => !o);
  };

  // Re-sync the view only when the popout opens or the value changes:
  // `parsed` is a new Date every render, so depending on it undid
  // Previous/Next month at once (seen on the 1st of a month).
  useEffect(() => {
    const p = parseDate(value);
    if (open && p) {
      setViewYear(p.getFullYear());
      setViewMonth(p.getMonth());
    }
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const prevMonth = () => setViewMonth(m => { if (m === 0) { setViewYear(y => y - 1); return 11; } return m - 1; });
  const nextMonth = () => setViewMonth(m => { if (m === 11) { setViewYear(y => y + 1); return 0; } return m + 1; });
  const handlePick = (date) => { onChange?.(formatISO(date)); setOpen(false); };

  const grid = getMonthGrid(viewYear, viewMonth);
  const today = new Date();
  const displayText = parsed
    ? parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

  return (
    <div ref={ref} className={clsx('relative w-full', className)}>
      {label && (
        <label className="block mb-1 text-sm font-medium" style={{ color: 'var(--color-text-default)' }}>
          {label}
        </label>
      )}
      <button
        type="button"
        disabled={disabled}
        aria-label={rest['aria-label'] ? `${rest['aria-label']}${displayText ? `: ${displayText}` : ''}` : undefined}
        onClick={toggleOpen}
        className={clsx('input w-full flex items-center gap-2 text-left', disabled && 'opacity-50 cursor-not-allowed')}
        style={{ height: 'var(--input-h)' }}
      >
        <CalendarDaysIcon className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
        <span className="flex-1 truncate" style={{ color: displayText ? 'var(--color-text-primary)' : 'var(--color-text-disabled)' }}>
          {displayText || 'Select date'}
        </span>
      </button>

      {open && (
        <div
          data-testid="date-picker-popout"
          style={{
            position: 'absolute', top: '100%',
            ...(alignRight ? { right: 0 } : { left: 0 }),
            width: '100%', minWidth: POPOUT_MIN_WIDTH,
            marginTop: 4, background: 'var(--color-surface)', border: '1px solid var(--neutral-200)',
            borderRadius: 'var(--card-radius)', zIndex: 50, padding: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, padding: '0 4px' }}>
            <button type="button" onClick={prevMonth} aria-label="Previous month"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4 }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 4l-4 4 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-default)' }}>
              {MONTHS[viewMonth]} {viewYear}
            </span>
            <button type="button" onClick={nextMonth} aria-label="Next month"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4 }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '0 4px', marginBottom: 2 }}>
            {DAYS_SHORT.map((d, i) => (
              <div key={i} style={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: 'var(--color-text-secondary)', paddingBottom: 4 }}>{d}</div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, padding: '0 4px' }}>
            {grid.map(({ date, currentMonth }, i) => {
              const isSelected = parsed && isSameDay(date, parsed);
              const isTodayCell = isSameDay(date, today);
              const afterMax = !!max && formatISO(date) > max;
              return (
                <button key={i} type="button" onClick={() => handlePick(date)}
                  disabled={afterMax}
                  aria-disabled={afterMax || undefined}
                  data-date={formatISO(date)}
                  style={{
                    height: 30, fontSize: 12, fontWeight: isSelected ? 600 : 400,
                    color: isSelected ? 'var(--white)' : (currentMonth ? 'var(--color-text-default)' : 'var(--color-text-muted)'),
                    background: isSelected ? 'var(--color-action-primary)' : (isTodayCell ? 'var(--neutral-100)' : 'transparent'),
                    border: 'none', borderRadius: 4, cursor: afterMax ? 'not-allowed' : 'pointer',
                    opacity: afterMax ? 0.35 : undefined,
                  }}
                >{date.getDate()}</button>
              );
            })}
          </div>
        </div>
      )}
      {error && <p className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>{error}</p>}
    </div>
  );
}

// ─── TimePicker ──────────────────────────────────────────────────────────────

const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const label = new Date(2000, 0, 1, h, m).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      opts.push({ val, label });
    }
  }
  return opts;
})();

/**
 * IDI-536: `typed` opts into a text field that accepts any minute ("9:02",
 * "09:02"), with the half-hour list as suggestions. Without it the picker is
 * the half-hour list only (scheduling surfaces). In typed mode onChange gets
 * the raw text; the caller validates it (e.g. utils/timeEntryEdit).
 */
export function TimePicker({ value, onChange, label, error, className, disabled, typed = false, 'aria-label': ariaLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Scroll to selected time when opening
  useEffect(() => {
    if (open && listRef.current && value) {
      const idx = TIME_OPTIONS.findIndex(o => o.val === value);
      if (idx > -1) {
        const el = listRef.current.children[idx];
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center' });
      }
    }
  }, [open, value]);

  const displayText = value
    ? (TIME_OPTIONS.find(o => o.val === value)?.label || value)
    : '';

  return (
    <div ref={ref} className={clsx('relative w-full', className)}>
      {label && (
        <label className="block mb-1 text-sm font-medium" style={{ color: 'var(--color-text-default)' }}>
          {label}
        </label>
      )}
      {typed ? (
        <div
          className={clsx('input w-full flex items-center gap-2', disabled && 'opacity-50 cursor-not-allowed')}
          style={{ height: 'var(--input-h)' }}
        >
          <ClockIcon className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
          <input
            type="text"
            inputMode="numeric"
            disabled={disabled}
            aria-label={ariaLabel || 'Time'}
            placeholder="HH:mm"
            value={value || ''}
            onFocus={() => setOpen(true)}
            onChange={(e) => onChange?.(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setOpen(false); }}
            className="flex-1 min-w-0 bg-transparent outline-none border-none p-0"
            style={{ color: 'var(--color-text-primary)', width: '5.5em' }}
          />
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel ? `${ariaLabel}${displayText ? `: ${displayText}` : ''}` : undefined}
          onClick={() => setOpen(o => !o)}
          className={clsx('input w-full flex items-center gap-2 text-left', disabled && 'opacity-50 cursor-not-allowed')}
          style={{ height: 'var(--input-h)' }}
        >
          <ClockIcon className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
          <span className="flex-1 truncate" style={{ color: displayText ? 'var(--color-text-primary)' : 'var(--color-text-disabled)' }}>
            {displayText || 'Select time'}
          </span>
        </button>
      )}

      {open && (
        <div
          ref={listRef}
          data-testid="time-picker-options"
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0,
            marginTop: 4, background: 'var(--color-surface)', border: '1px solid var(--neutral-200)',
            borderRadius: 'var(--card-radius)', zIndex: 50,
            maxHeight: 200, overflowY: 'auto', padding: '4px 0',
          }}
        >
          {TIME_OPTIONS.map(({ val, label: lbl }) => (
            <button
              key={val}
              type="button"
              onClick={() => { onChange?.(val); setOpen(false); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '6px 12px', fontSize: 13, border: 'none', cursor: 'pointer',
                background: val === value ? 'var(--red-100)' : 'transparent',
                color: val === value ? 'var(--color-action-primary)' : 'var(--color-text-default)',
                fontWeight: val === value ? 600 : 400,
              }}
              onMouseEnter={(e) => { if (val !== value) e.currentTarget.style.background = 'var(--neutral-50)'; }}
              onMouseLeave={(e) => { if (val !== value) e.currentTarget.style.background = 'transparent'; }}
            >
              {lbl}
            </button>
          ))}
        </div>
      )}
      {error && <p className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>{error}</p>}
    </div>
  );
}

export default DatePicker;
